/**
 * Scoring Matrix Settings Tab
 *
 * Shows all tasks and competencies with their scoring criteria in a matrix view.
 * Includes a button to open a print-optimized version.
 * Supports inline editing of score criteria descriptions per task/competency.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { competenciesApi, type ScoringMatrix, type CompetencyMatrixItem, type TaskMatrixItem, type ScoreCriterion } from '../../../lib/competenciesApi';
import { scoreCriteriaApi } from '../../../lib/settingsApi';
import type { UpdateScoreCriteriaRequest } from '@functieloonhuis/shared';

const SCORE_LABELS: Record<number, string> = {
  1: 'Onvoldoende',
  2: 'Matig',
  3: 'Voldoende',
  4: 'Goed',
  5: 'Uitzonderlijk',
};

// Default percentage ranges for score levels (used when creating new criteria)
const DEFAULT_SCORE_RANGES: Record<number, { min: number; max: number; scorePercentage: number }> = {
  1: { min: 0, max: 39, scorePercentage: 0 },
  2: { min: 40, max: 54, scorePercentage: 25 },
  3: { min: 55, max: 69, scorePercentage: 50 },
  4: { min: 70, max: 84, scorePercentage: 75 },
  5: { min: 85, max: 100, scorePercentage: 100 },
};

// Types for edit modal
interface EditingCell {
  itemId: number;
  itemName: string;
  itemType: 'task' | 'competency';
  criteria: ScoreCriterion[];
}

interface EditCriteriaModalProps {
  editing: EditingCell;
  onClose: () => void;
  onSave: (criteria: ScoreCriterion[]) => void;
  isSaving: boolean;
}

function EditCriteriaModal({ editing, onClose, onSave, isSaving }: EditCriteriaModalProps) {
  // Initialize local state with all 5 score levels
  const [localCriteria, setLocalCriteria] = useState<ScoreCriterion[]>(() => {
    // Ensure all 5 score levels are present
    return [1, 2, 3, 4, 5].map((level): ScoreCriterion => {
      const existing = editing.criteria.find((c) => c.scoreLevel === level);
      return existing || { scoreLevel: level, label: SCORE_LABELS[level] || `Score ${level}`, description: '' };
    });
  });

  const handleDescriptionChange = (scoreLevel: number, description: string) => {
    setLocalCriteria((prev) =>
      prev.map((c) => (c.scoreLevel === scoreLevel ? { ...c, description } : c))
    );
  };

  const handleSave = () => {
    onSave(localCriteria);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        {/* Modal */}
        <div className="relative transform overflow-hidden rounded-lg bg-white shadow-xl transition-all w-full max-w-4xl">
          {/* Header */}
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Score criteria bewerken
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {editing.itemType === 'task' ? 'Taak' : 'Competentie'}: <span className="font-medium">{editing.itemName}</span>
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Sluiten</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-4">
              {localCriteria.map((criterion) => (
                <div key={criterion.scoreLevel} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm">
                      {criterion.scoreLevel}
                    </span>
                    <span className="font-medium text-gray-900">{criterion.label}</span>
                  </div>
                  <textarea
                    value={criterion.description}
                    onChange={(e) => handleDescriptionChange(criterion.scoreLevel, e.target.value)}
                    placeholder={`Beschrijving voor score ${criterion.scoreLevel} (${criterion.label})...`}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Annuleren
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {isSaving && (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              )}
              Opslaan
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface CompetencyMatrixTableProps {
  title: string;
  competencies: CompetencyMatrixItem[];
  badgeColor: string;
  onEditClick: (item: CompetencyMatrixItem) => void;
}

function CompetencyMatrixTable({ title, competencies, badgeColor, onEditClick }: CompetencyMatrixTableProps) {
  if (competencies.length === 0) {
    return null;
  }

  return (
    <div className="mb-8">
      <h3 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <span className={`inline-block w-3 h-3 rounded-full ${badgeColor}`}></span>
        {title} ({competencies.length})
      </h3>
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48 sticky left-0 bg-gray-50 z-10">
                Competentie
              </th>
              {[1, 2, 3, 4, 5].map((score) => (
                <th key={score} className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[180px]">
                  <div className="font-bold text-gray-900">{score}</div>
                  <div className="font-normal normal-case">{SCORE_LABELS[score]}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {competencies.map((comp, index) => (
              <tr key={comp.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-4 py-3 align-top sticky left-0 bg-inherit z-10 border-r border-gray-100">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-medium text-gray-900">{comp.name}</div>
                      {comp.beschrijving && (
                        <div className="text-xs text-gray-500 mt-1 max-w-xs">
                          {comp.beschrijving}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => onEditClick(comp)}
                      className="flex-shrink-0 p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      title="Score criteria bewerken"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                  </div>
                </td>
                {[1, 2, 3, 4, 5].map((score) => {
                  const criterion = comp.criteria.find(c => c.scoreLevel === score);
                  return (
                    <td key={score} className="px-3 py-3 align-top text-xs text-gray-700">
                      {criterion ? (
                        <div className="leading-relaxed">{criterion.description}</div>
                      ) : (
                        <div className="text-gray-400 italic">Geen criteria</div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface TaskMatrixTableProps {
  tasks: TaskMatrixItem[];
  onEditClick: (task: TaskMatrixItem) => void;
}

function TaskMatrixTable({ tasks, onEditClick }: TaskMatrixTableProps) {
  if (tasks.length === 0) {
    return null;
  }

  return (
    <div className="mb-8">
      <h3 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <span className="inline-block w-3 h-3 rounded-full bg-green-500"></span>
        Taken ({tasks.length})
      </h3>
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48 sticky left-0 bg-gray-50 z-10">
                Taak
              </th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                Weging
              </th>
              {[1, 2, 3, 4, 5].map((score) => (
                <th key={score} className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[180px]">
                  <div className="font-bold text-gray-900">{score}</div>
                  <div className="font-normal normal-case">{SCORE_LABELS[score]}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tasks.map((task, index) => (
              <tr key={task.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-4 py-3 align-top sticky left-0 bg-inherit z-10 border-r border-gray-100">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-medium text-gray-900">{task.name}</div>
                      {task.beschrijving && (
                        <div className="text-xs text-gray-500 mt-1 max-w-xs">
                          {task.beschrijving}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => onEditClick(task)}
                      className="flex-shrink-0 p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      title="Score criteria bewerken"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                  </div>
                </td>
                <td className="px-3 py-3 text-center text-sm font-medium text-gray-900">
                  {task.weging}
                </td>
                {[1, 2, 3, 4, 5].map((score) => {
                  const criterion = task.criteria.find(c => c.scoreLevel === score);
                  return (
                    <td key={score} className="px-3 py-3 align-top text-xs text-gray-700">
                      {criterion ? (
                        <div className="leading-relaxed">{criterion.description}</div>
                      ) : (
                        <div className="text-gray-400 italic">Geen criteria</div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function CompetencyMatrixTab() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<EditingCell | null>(null);

  const { data: matrix, isLoading, error } = useQuery<ScoringMatrix>({
    queryKey: ['scoring', 'matrix'],
    queryFn: () => competenciesApi.getScoringMatrix(),
  });

  // Mutation for saving score criteria
  const saveMutation = useMutation({
    mutationFn: async (data: { itemId: number; itemType: 'task' | 'competency'; criteria: ScoreCriterion[] }) => {
      const request: UpdateScoreCriteriaRequest = {
        contextType: data.itemType,
        contextId: data.itemId,
        criteria: data.criteria.map((c) => {
          const range = DEFAULT_SCORE_RANGES[c.scoreLevel] || { min: 0, max: 100, scorePercentage: 50 };
          return {
            scoreLevel: c.scoreLevel,
            label: c.label,
            description: c.description,
            minPercentage: range.min,
            maxPercentage: range.max,
            scorePercentage: range.scorePercentage,
          };
        }),
      };
      return scoreCriteriaApi.update(request);
    },
    onSuccess: () => {
      // Invalidate and refetch the scoring matrix
      queryClient.invalidateQueries({ queryKey: ['scoring', 'matrix'] });
      setEditing(null);
    },
  });

  const handleEditTask = (task: TaskMatrixItem) => {
    setEditing({
      itemId: task.id,
      itemName: task.name,
      itemType: 'task',
      criteria: task.criteria,
    });
  };

  const handleEditCompetency = (comp: CompetencyMatrixItem) => {
    setEditing({
      itemId: comp.id,
      itemName: comp.name,
      itemType: 'competency',
      criteria: comp.criteria,
    });
  };

  const handleSave = (criteria: ScoreCriterion[]) => {
    if (!editing) return;
    saveMutation.mutate({
      itemId: editing.itemId,
      itemType: editing.itemType,
      criteria,
    });
  };

  const handleOpenPrintVersion = () => {
    window.open('/settings/competency-matrix/print', '_blank');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Laden...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-sm text-red-700">Fout bij het laden van de scoringsmatrix.</p>
      </div>
    );
  }

  if (!matrix) {
    return null;
  }

  const totalCompetencies = matrix.competencies.basis.length + matrix.competencies.vak.length;
  const totalTasks = matrix.tasks.length;

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Scoringsmatrix</h2>
          <p className="text-sm text-gray-500">
            Overzicht van alle taken en competenties met scoringscriteria per niveau (1-5).
            <span className="ml-1 text-blue-600">Klik op het potloodje om criteria te bewerken.</span>
          </p>
        </div>
        <button
          onClick={handleOpenPrintVersion}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <svg className="mr-2 -ml-1 w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
          </svg>
          Printversie openen
        </button>
      </div>

      {/* Score Legend */}
      <div className="bg-blue-50 rounded-lg p-4 mb-6">
        <p className="text-sm font-medium text-blue-800 mb-2">Scorelegenda:</p>
        <div className="flex flex-wrap gap-4 text-sm">
          {[1, 2, 3, 4, 5].map((score) => (
            <span key={score} className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full border-2 border-blue-400 bg-white flex items-center justify-center text-sm font-bold text-blue-600">
                {score}
              </span>
              <span className="text-gray-700">{SCORE_LABELS[score]}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Tasks */}
      <TaskMatrixTable tasks={matrix.tasks} onEditClick={handleEditTask} />

      {/* Basis Competencies */}
      <CompetencyMatrixTable
        title="Basiscompetenties"
        competencies={matrix.competencies.basis}
        badgeColor="bg-purple-500"
        onEditClick={handleEditCompetency}
      />

      {/* Vak Competencies */}
      <CompetencyMatrixTable
        title="Vakcompetenties"
        competencies={matrix.competencies.vak}
        badgeColor="bg-indigo-500"
        onEditClick={handleEditCompetency}
      />

      {/* Summary */}
      <div className="mt-6 pt-4 border-t border-gray-200 flex items-center justify-between text-sm text-gray-500">
        <div className="flex gap-4">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-green-500"></span>
            Taken: {totalTasks}
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-purple-500"></span>
            Basiscompetenties: {matrix.competencies.basis.length}
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
            Vakcompetenties: {matrix.competencies.vak.length}
          </span>
          <span>
            Totaal: {totalTasks + totalCompetencies} items
          </span>
        </div>
        {matrix.generatedAt && (
          <span className="text-xs text-gray-400">
            Laatst bijgewerkt: {new Date(matrix.generatedAt).toLocaleString('nl-NL')}
          </span>
        )}
      </div>

      {/* Edit Modal */}
      {editing && (
        <EditCriteriaModal
          editing={editing}
          onClose={() => setEditing(null)}
          onSave={handleSave}
          isSaving={saveMutation.isPending}
        />
      )}

      {/* Error Toast */}
      {saveMutation.isError && (
        <div className="fixed bottom-4 right-4 bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg">
          <p className="text-sm text-red-700">Fout bij het opslaan van de criteria.</p>
        </div>
      )}
    </div>
  );
}

export default CompetencyMatrixTab;
